import React, { useState, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Switch,
  Route,
  NavLink,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';
import Input from 'components/Input';
import Button from 'components/Button';
import { useHasura } from 'context/ApiState';
import Select from 'components/Select';
import { useWrappedUrqlMutation } from 'toolbox';
import { useUpdateBankMutation } from './UpdateBank.generated';

const BankRiskCard = ({ bank }) => {
  const { path, url } = useRouteMatch();
  const location = useLocation();

  const updateBank = useWrappedUrqlMutation(useUpdateBankMutation);

  const tabs = [
    { id: 'sp', title: 'Standard & Poor', component: StandardAndPoors },
    { id: 'moodys', title: 'Moodys', component: Moodys },
    { id: 'fitch', title: 'Fitch', component: Fitch },
    { id: 'performance', title: 'Performance', component: Performance },
    // { id: 'basel', title: 'Basel III', component: BaselIII },
    // { id: 'balancesheet', title: 'Balance Sheet', component: BalanceSheet },
  ];
  const DefaultTabComponent = tabs[0].component;

  const [formData, setFormData] = useState(bank);

  const changeHandler = (target: string, value: string) => {
    setFormData({ ...formData, [target]: value });
  };

  const undoChanges = () => setFormData(bank);

  const saveChanges = async () => {
    try {
      await updateBank.dispatch({
        bank_id: formData?.id,
        modification: {
          risk_rating_sp_date: formData?.risk_rating_sp_date,
          risk_rating_short_sp: formData?.risk_rating_short_sp,
          risk_rating_long_sp: formData?.risk_rating_long_sp,
          risk_outlook_sp: formData?.risk_outlook_sp,

          risk_rating_fitch_date: formData?.risk_rating_fitch_date,
          risk_rating_short_fitch: formData?.risk_rating_short_fitch,
          risk_rating_long_fitch: formData?.risk_rating_long_fitch,
          risk_outlook_fitch: formData?.risk_outlook_fitch,

          risk_rating_moodys_date: formData?.risk_rating_moodys_date,
          risk_rating_long_moodys: formData?.risk_rating_long_moodys,
          risk_outlook_moodys: formData?.risk_outlook_moodys,

          // risk_5y_cds_spread: formData?.risk_5y_cds_spread,
          risk_total_equity: formData?.risk_total_equity, //new
          risk_cet_capital_ratio: formData?.risk_cet_capital_ratio, //changed
          risk_leverage_ratio: formData?.risk_leverage_ratio,
          risk_liquidity_coverage_ratio:
            formData?.risk_liquidity_coverage_ratio,
          risk_share_movement: formData?.risk_share_movement,
          risk_market_cap: formData?.risk_market_cap,
          risk_base_currency: formData?.risk_base_currency,
        },
      });
    } catch (err) {
      console.warn(err);
    }
  };

  return (
    <div className="flex-col card">
      <aside className="z-10 w-full p-6 pb-0 bg-white border-b border-gray-200">
        <h2 className="mb-4 text-lg font-semibold text-gray-800">
          Risk Ratings
        </h2>

        <div className="flex -ml-1">
          {tabs.map(({ title, id }, i) => (
            <NavLink
              key={id}
              to={`${url}/${id}`}
              isActive={(match, loc) =>
                !!match || (path == loc.pathname && i == 0)
              }
              className="px-2 py-3 mr-3 text-sm font-semibold border-b-2 border-white"
              activeClassName="border-jcap-500"
            >
              {title}
            </NavLink>
          ))}
        </div>
      </aside>

      <main className="p-6 pb-12 overflow-hidden">
        <AnimatePresence exitBeforeEnter initial={false}>
          <Switch location={location} key={path}>
            {tabs.map(({ component: Component, id }) => (
              <Route key={id} path={`${path}/${id}`}>
                <motion.div
                  className="w-full"
                  variants={motionVariants}
                  initial="initial"
                  animate="in"
                  exit="out"
                >
                  <Component
                    formData={formData}
                    changeHandler={changeHandler}
                  />
                </motion.div>
              </Route>
            ))}
            <Route>
              <DefaultTabComponent
                formData={formData}
                changeHandler={changeHandler}
              />
            </Route>
          </Switch>
        </AnimatePresence>
      </main>
      <footer>
        <Button
          onClick={undoChanges}
          className="mr-3 btn btn-white"
          disabled={JSON.stringify(formData) === JSON.stringify(bank)}
        >
          Undo Changes
        </Button>
        <Button
          onClick={saveChanges}
          className="btn"
          disabled={JSON.stringify(formData) === JSON.stringify(bank)}
        >
          Save
        </Button>
      </footer>
    </div>
  );
};

const motionVariants = {
  initial: {
    opacity: 0,
    y: '-100%',
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
    },
  },
  out: {
    opacity: 0,
    y: '-100%',
    transition: {
      duration: 0.1,
    },
  },
};

const StandardAndPoors = ({ formData, changeHandler }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <Input
        label="Short Term Risk Rating"
        value={formData.risk_rating_short_sp || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_short_sp', event.currentTarget.value)
        }
      />
      <Input
        label="Long Term Risk Rating"
        value={formData.risk_rating_long_sp || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_long_sp', event.currentTarget.value)
        }
      />
      <Input
        label="Outlook"
        value={formData.risk_outlook_sp || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_outlook_sp', event.currentTarget.value)
        }
      />
      <Input
        label="Risk Rating Date"
        wrapperClasses="col-start-1"
        value={formData.risk_rating_sp_date || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_sp_date', event.currentTarget.value)
        }
      />
    </div>
  );
};

const Moodys = ({ formData, changeHandler }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <Input label="Short Term Risk Rating" placeholder="Not Used" disabled />
      <Input
        label="Long Term Risk Rating"
        wrapperClasses="col-start-2"
        value={formData.risk_rating_long_moodys || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_long_moodys', event.currentTarget.value)
        }
      />
      <Input
        label="Outlook"
        wrapperClasses="col-start-3"
        value={formData.risk_outlook_moodys || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_outlook_moodys', event.currentTarget.value)
        }
      />
      <Input
        label="Risk Rating Date"
        wrapperClasses="col-start-1"
        value={formData.risk_rating_moodys_date || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_moodys_date', event.currentTarget.value)
        }
      />
    </div>
  );
};

const Fitch = ({ formData, changeHandler }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <Input
        label="Short Term Risk Rating"
        value={formData.risk_rating_short_fitch || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_short_fitch', event.currentTarget.value)
        }
      />
      <Input
        label="Long Term Risk Rating"
        value={formData.risk_rating_long_fitch || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_long_fitch', event.currentTarget.value)
        }
      />
      <Input
        label="Outlook"
        value={formData.risk_outlook_fitch || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_outlook_fitch', event.currentTarget.value)
        }
      />
      <Input
        label="Risk Rating Date"
        wrapperClasses="col-start-1"
        value={formData.risk_rating_fitch_date || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_rating_fitch_date', event.currentTarget.value)
        }
      />
    </div>
  );
};

const Performance = ({ formData, changeHandler }) => {
  const { currencies } = useHasura();

  return (
    <div className="grid grid-cols-3 gap-6">
      <Input
        label="Total Shareholder Funds (Equity)"
        value={formData.risk_total_equity || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_total_equity', event.currentTarget.value)
        }
      />
      <Select
        label="Currency"
        value={formData.risk_base_currency || 'AUD'}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_base_currency', event.currentTarget.value)
        }
      >
        {currencies?.map((curr) => (
          <option key={curr?.id} value={curr?.id}>
            {curr?.prefix}
          </option>
        ))}
      </Select>
      <Input
        label="Market Cap Min"
        wrapperClasses="col-start-1"
        value={formData.risk_market_cap || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_market_cap', event.currentTarget.value)
        }
      />
      <Input
        label="Share Price Change % (1 month)"
        value={formData.risk_share_movement || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_share_movement', event.currentTarget.value)
        }
      />
      <Input
        label="CET 1 Capital Ratio"
        wrapperClasses="col-start-1"
        value={formData.risk_cet_capital_ratio || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_cet_capital_ratio', event.currentTarget.value)
        }
      />
      <Input
        label="Levarage Ratio"
        value={formData.risk_leverage_ratio || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler('risk_leverage_ratio', event.currentTarget.value)
        }
      />
      <Input
        label="Liquidity Coverage Ratio"
        value={formData.risk_liquidity_coverage_ratio || ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          changeHandler(
            'risk_liquidity_coverage_ratio',
            event.currentTarget.value,
          )
        }
      />
    </div>
  );
};

// const BaselIII = (
//   {
//     /*formData, changeHandler*/
//   }
// ) => {
//   return (
//     <div className="grid grid-cols-3 gap-6">
//       <Input label="Outlook" />
//     </div>
//   );

export default BankRiskCard;
